import * as React from 'react';

import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';

import { Grid, IconButton, TextField, Tooltip } from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';
import { Navigate } from 'react-router-dom';
import ControlledDatePicker from '../ControlledDatePicker';
import ControlledCascadingSelect from '../ControlledCascadingSelect';
import ControlledAircraftSelect from '../ControlledAircraftSelect';
import ControlledAirportSelect from '../ControlledAirportSelect';
import ControlledTextField from '../ControlledTextField';
import ControlledSelect from '../ControlledSelect';

import ReadOnlyField from '../ReadOnlyField';

import { Incident, IncidentStatus } from '../../core/api/clients';

import { useIncidentFieldOptions } from '../../core/context/IncidentEventFieldOptions';
import IncidentWorkflowStatus from './IncidentWorkflowStatus';

interface IncidentFormFieldsProps {
  incidentStatus?: IncidentStatus;
  incidentAssessedOn?: string | null;
  readOnly?: boolean;
}

function IncidentFormFields({ incidentAssessedOn, incidentStatus, readOnly }: IncidentFormFieldsProps): React.JSX.Element {
  const { control, setValue } = useFormContext<Incident>();
  const { eventOptions, aircrafts, airports } = useIncidentFieldOptions();

  const getValidDateTime = (assessedOn: string | null | undefined): string => {
    const date = DateTime.fromISO(assessedOn ?? '').toLocaleString(DateTime.DATETIME_SHORT);
    if (date === 'Invalid DateTime') return '';
    return date;
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={4}>
        <ControlledTextField<Incident>
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="title"
          control={control}
          rules={{ required: true }}
          disableEditPopper
        />
      </Grid>
      <Grid item xs={2}>
        <ControlledSelect
          name="riskRating"
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          control={control}
          rules={{ required: true }}
          disableEditPopper
          useMenuItemName
          menuItems={[
            { name: 'A1 - Negligible', value: 'A1' },
            { name: 'B1 - Negligible', value: 'B1' },
            { name: 'C1 - Negligible', value: 'C1' },
            { name: 'D1 - Negligible', value: 'D1' },
            { name: 'C2 - Minor', value: 'C2' },
            { name: 'D2 - Minor', value: 'D2' },
            { name: 'D3 - Minor', value: 'D3' },
            { name: 'B2 - Low', value: 'B2' },
            { name: 'C3 - Low', value: 'C3' },
            { name: 'D4 - Low', value: 'D4' },
            { name: 'A2 - Moderate', value: 'A2' },
            { name: 'B3 - Moderate', value: 'B3' },
            { name: 'C4 - Moderate', value: 'C4' },
            { name: 'D5 - Moderate', value: 'D5' },
            { name: 'A3 - High', value: 'A3' },
            { name: 'B4 - High', value: 'B4' },
            { name: 'C5 - High', value: 'C5' },
            { name: 'A4 - Extreme', value: 'A4' },
            { name: 'A5 - Extreme', value: 'A5' },
            { name: 'B5 - Extreme', value: 'B5' },
          ]}
        />
      </Grid>
      <Grid item xs={2}>
        <ControlledSelect
          name="severityCategory"
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          control={control}
          rules={{ required: true }}
          disableEditPopper
          useMenuItemName
          menuItems={[
            { name: 'A - Accident or Incident', value: 'A' },
            { name: 'B - Employee or Customer Injury', value: 'B' },
            { name: 'C - Airworthiness', value: 'C' },
            { name: 'D - Security', value: 'D' },
            { name: 'E - Operational Events', value: 'E' },
            { name: 'F - Systems or Processes', value: 'F' },
            { name: 'G - Regulatory', value: 'G' },
            { name: 'H - Brand', value: 'H' },
            { name: 'I - Environment', value: 'I' },
            { name: 'J - OSHA', value: 'J' },
            { name: 'K - Audit Finding', value: 'K' },
          ]}
        />
      </Grid>
      <Grid display="flex" item md justifyContent="center" alignItems="center">
        <Tooltip title="Risk Assessment Matrix Reference">
          <IconButton
            href="api/metadata/help?formName=risk-assessment-matrix.pdf"
            target="_blank"
          >
            <InfoIcon color="primary" />
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item md={1.6}>
        <ReadOnlyField
          name="Assessed On"
          value={`${getValidDateTime(incidentAssessedOn)}`}
          textProps={{ overflow: 'hidden', height: '1.5em' }}
        />
      </Grid>
      <Grid item md={1.5}>
        <ControlledDatePicker
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="eventDate"
          control={control}
          rules={{ required: true }}
          disableEditPopper
        />
      </Grid>
      <Grid item xs={12}>
        <ControlledCascadingSelect<Incident>
          groupName="eventType"
          name="eventType"
          control={control}
          fieldNames={['eventType', 'eventType2', 'eventType3']}
          options={eventOptions}
          rules={{ required: true }}
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          setValue={setValue}
          disableEditPopper
        />
      </Grid>
      <Grid item md={1.5}>
        <ControlledAircraftSelect<Incident>
          options={aircrafts ?? []}
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="aircraftNumber"
          control={control}
          rules={{ required: true }}
          disableEditPopper
        />
      </Grid>
      <Grid item md={1.5}>
        <ControlledTextField
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="flightNumber"
          control={control}
          rules={{ required: true }}
          disableEditPopper
        />
      </Grid>
      <Grid item md>
        <ControlledAirportSelect<Incident>
          options={airports ?? []}
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="departureAirport"
          control={control}
          rules={{ required: true }}
          disableEditPopper
        />
      </Grid>
      <Grid item md>
        <ControlledAirportSelect<Incident>
          options={airports ?? []}
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="scheduledArrivalAirport"
          control={control}
          rules={{ required: true }}
          disableEditPopper
        />
      </Grid>
      <Grid item md>
        <ControlledAirportSelect<Incident>
          options={airports ?? []}
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="landingAirport"
          control={control}
          rules={{ required: true }}
          disableEditPopper
        />
      </Grid>
      <Grid item md={12}>
        <ControlledTextField
          readOnly={incidentStatus === IncidentStatus.Closed || readOnly}
          name="comments"
          control={control}
          disableEditPopper
        />
      </Grid>
      <Grid item md={12}>
        <IncidentWorkflowStatus />
      </Grid>
    </Grid>
  );
}

export default IncidentFormFields;
